import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useQuery } from "urql";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useDebounce from "@/hooks/useDebounce";
import { searchVideosQuery, videoQuery } from "@/routes/articles/queries";
import { cn, cx } from "@/utils/cx";

function useVideoId(videoId?: string | null) {
    const [result] = useQuery({
        query: videoQuery,
        variables: {
            id: videoId!,
        },
        pause: !videoId,
    });
    return result.data?.video;
}

export function VideoIdInput({
    className,
    videoId,
    onVideoIdChange,
}: {
    className?: string;
    videoId?: string | null;
    onVideoIdChange: (videoId: string | null) => void;
}) {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const [result] = useQuery({
        query: searchVideosQuery,
        variables: {
            input: {
                query: debouncedQuery,
                limit: 10,
                offset: 0,
            },
        },
        pause: !debouncedQuery,
    });
    const fetchedVideo = useVideoId(videoId);
    const selectedVideo = videoId != null ? fetchedVideo : null;
    const emptyList = selectedVideo == null ? [] : [selectedVideo];
    const videos: { id: string; title: string | null }[] =
        result.data?.searchVideos.items ?? emptyList;

    const [open, setOpen] = useState(false);

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="justify-between"
                    >
                        {selectedVideo
                            ? `(${selectedVideo.id}) ${selectedVideo.title}`
                            : "Find video..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[600px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search for videos by title or id..."
                            value={query}
                            onInput={e => setQuery(e.currentTarget.value)}
                        />
                        <CommandList>
                            <CommandEmpty>No matching videos found.</CommandEmpty>
                            <CommandGroup>
                                {videos.map(video => (
                                    <CommandItem
                                        key={video.id}
                                        value={video.id}
                                        onSelect={currentValue => {
                                            onVideoIdChange(
                                                selectedVideo?.id === currentValue
                                                    ? null
                                                    : currentValue
                                            );
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                selectedVideo?.id === video.id
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        {video.title}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}
